import React, { useState, useRef, useEffect } from 'react';
import './FileMenu.css';
import { useClickOutside } from '../../hooks/useClickOutside';
import { useEscapeKey } from '../../hooks/useEscapeKey';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArchive,
  faDiagramProject,
  faChevronDown,
  faChevronRight,
  faClockRotateLeft,
  faClone,
  faFloppyDisk,
  faFolderOpen,
  faLayerGroup,
  faPenToSquare,
  faPlus,
  faRotateLeft,
  faTrashCan,
} from '@fortawesome/free-solid-svg-icons';
import { useI18n } from '../../i18n/I18nContext';

// Props for the FileMenu component.
export interface FileMenuProps {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
  loadedScenario: boolean;
  canDeleteScenario: boolean;
  scenarioReadOnly?: boolean;
  onLoad: () => void;
  onNew: () => void;
  onDuplicateScenario: () => void;
  onSaveScenarioBaseState: () => void;
  onDelete: () => void;
  onArchiveScenario: () => void;
  onEditScenario: () => void;
  onShowArchivedScenarios: () => void;
  recentScenarioNames: string[];
  onLoadRecent: (name: string) => void;
  onReset: () => void;
}

// FileMenu encapsulates the Scenario dropdown menu and handles its open/close state.
const FileMenu: React.FC<FileMenuProps> = ({
  open,
  onToggle,
  onClose,
  loadedScenario,
  canDeleteScenario,
  scenarioReadOnly = false,
  onLoad,
  onNew,
  onDuplicateScenario,
  onSaveScenarioBaseState,
  onDelete,
  onArchiveScenario,
  onEditScenario,
  onShowArchivedScenarios,
  recentScenarioNames,
  onLoadRecent,
  onReset,
}) => {
  const { t } = useI18n();
  const [showRecentMenu, setShowRecentMenu] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useClickOutside(menuRef, () => {
    onClose();
    setShowRecentMenu(false);
  }, open);
  useEscapeKey(() => {
    onClose();
    setShowRecentMenu(false);
  }, open);

  useEffect(() => {
    if (!open) {
      setShowRecentMenu(false);
    }
  }, [open]);

  return (
    <div className="file-menu" ref={menuRef}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={e => {
          e.stopPropagation();
          onToggle();
        }}
      >
        <FontAwesomeIcon icon={faDiagramProject} /> {t('menus.scenario')} <FontAwesomeIcon icon={faChevronDown} />
      </button>
      {open && (
        <div className="dropdown-menu" role="menu">
          <button
            type="button"
            onClick={() => {
              onNew();
              onClose();
              setShowRecentMenu(false);
            }}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faPlus} /></span>
            <span className="menu-item-label">{t('menus.new')}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              onLoad();
              onClose();
              setShowRecentMenu(false);
            }}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faFolderOpen} /></span>
            <span className="menu-item-label">{t('menus.load')}</span>
          </button>
          <div
            className={`dropdown-submenu ${showRecentMenu ? 'open' : ''}`}
            onMouseEnter={() => setShowRecentMenu(true)}
            onMouseLeave={() => setShowRecentMenu(false)}
          >
            <button
              type="button"
              className="submenu-trigger"
              onClick={() => setShowRecentMenu((current) => !current)}
            >
              <span className="menu-item-icon"><FontAwesomeIcon icon={faClockRotateLeft} /></span>
              <span className="menu-item-label">{t('menus.loadRecent')}</span>
              <span className="submenu-chevron"><FontAwesomeIcon icon={faChevronRight} /></span>
            </button>
            {showRecentMenu && (
              <div className="dropdown-menu dropdown-submenu-panel" role="menu">
                {recentScenarioNames.length > 0 ? recentScenarioNames.slice(0, 10).map((name) => (
                  <button
                    key={name}
                    type="button"
                    className="dropdown-menu__recent-button"
                    title={name}
                    onClick={() => {
                      onLoadRecent(name);
                      onClose();
                      setShowRecentMenu(false);
                    }}
                  >
                    <span className="menu-item-icon"><FontAwesomeIcon icon={faClockRotateLeft} /></span>
                    <span className="menu-item-label">{name}</span>
                  </button>
                )) : (
                  <div className="dropdown-empty">{t('menus.noRecentScenarios')}</div>
                )}
              </div>
            )}
          </div>
          <div className="dropdown-divider" />
          <button
            type="button"
            onClick={() => {
              onDuplicateScenario();
              onClose();
              setShowRecentMenu(false);
            }}
            disabled={!loadedScenario}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faClone} /></span>
            <span className="menu-item-label">{t('menus.duplicate')}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              onEditScenario();
              onClose();
              setShowRecentMenu(false);
            }}
            disabled={!loadedScenario || scenarioReadOnly}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faPenToSquare} /></span>
            <span className="menu-item-label">{t('menus.edit')}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              onSaveScenarioBaseState();
              onClose();
              setShowRecentMenu(false);
            }}
            disabled={!loadedScenario || scenarioReadOnly}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faFloppyDisk} /></span>
            <span className="menu-item-label">{t('menus.saveScenarioBaseState')}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              onReset();
              onClose();
              setShowRecentMenu(false);
            }}
            disabled={!loadedScenario || scenarioReadOnly}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faRotateLeft} /></span>
            <span className="menu-item-label">{t('menus.reset')}</span>
          </button>
          <div className="dropdown-divider" />
          <button
            type="button"
            onClick={() => {
              onArchiveScenario();
              onClose();
              setShowRecentMenu(false);
            }}
            disabled={!loadedScenario || !canDeleteScenario || scenarioReadOnly}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faArchive} /></span>
            <span className="menu-item-label">{t('menus.archiveScenario')}</span>
          </button>
          <button
            type="button"
            onClick={() => {
              onShowArchivedScenarios();
              onClose();
              setShowRecentMenu(false);
            }}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faLayerGroup} /></span>
            <span className="menu-item-label">{t('menus.archivedScenarios')}</span>
          </button>
          <div className="dropdown-divider" />
          <button
            type="button"
            onClick={() => {
              onDelete();
              onClose();
              setShowRecentMenu(false);
            }}
            disabled={!loadedScenario || !canDeleteScenario || scenarioReadOnly}
          >
            <span className="menu-item-icon"><FontAwesomeIcon icon={faTrashCan} /></span>
            <span className="menu-item-label">{t('menus.deleteScenario')}</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default FileMenu;


